import React, { useState } from "react";
import {
  Button, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter,
  Input, Textarea, Label, Badge, Separator, Skeleton, Spinner,
  Alert, AlertTitle, AlertDescription,
  Dialog, DialogTrigger, DialogContent, DialogHeader, DialogFooter, DialogTitle, DialogDescription, DialogClose,
  Tabs, TabsList, TabsTrigger, TabsContent,
  Tooltip, TooltipTrigger, TooltipContent, TooltipProvider,
  Switch,
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
  Table, TableHeader, TableBody, TableRow, TableHead, TableCell,
  Avatar, AvatarFallback, Progress, StatTile, EmptyState, PageHeader, Eyebrow,
} from "@/components/ui";
import { toast } from "sonner";
import { MessageSquare, Users, TrendingUp, Zap, Inbox, MoreHorizontal, Sparkles } from "lucide-react";

// Living reference / QA surface for the deck-socios kit. Route: /kit-preview
export default function KitPreview() {
  const [on, setOn] = useState(true);
  return (
    <TooltipProvider>
      <div data-kit="" className="min-h-screen bg-background px-6 py-10 font-sans text-foreground">
        <div className="mx-auto max-w-6xl space-y-10">
          <PageHeader
            eyebrow="Design System · deck-socios"
            title="WhatsPRO — kit de componentes"
            subtitle="Fuente de verdad visual para la barrida. Poppins, azul #0DA15A, cards radio 16 con sombra suave, gradiente con moderación."
            actions={<Button variant="gradient"><Sparkles /> Acción hero</Button>}
          />

          {/* KPIs */}
          <section className="grid grid-cols-2 gap-4 md:grid-cols-4">
            <StatTile label="Conversaciones" value="12.480" icon={MessageSquare} tone="brand" delta="+8,2%" deltaTone="up" hint="vs. mes previo" />
            <StatTile label="Asesores activos" value="7" icon={Users} hint="de 9 totales" />
            <StatTile label="Tasa de respuesta" value="94%" icon={TrendingUp} tone="success" delta="+3%" deltaTone="up" />
            <StatTile label="Sin atender" value="23" icon={Inbox} tone="coral" delta="+5" deltaTone="down" hint="requieren acción" />
          </section>

          {/* Buttons */}
          <section className="space-y-3">
            <Eyebrow>Botones</Eyebrow>
            <div className="flex flex-wrap items-center gap-3">
              <Button>Primario</Button>
              <Button variant="gradient"><Zap /> Gradiente</Button>
              <Button variant="outline">Outline</Button>
              <Button variant="subtle">Subtle</Button>
              <Button variant="ghost">Ghost</Button>
              <Button variant="success">Éxito</Button>
              <Button variant="destructive">Eliminar</Button>
              <Button variant="link">Link</Button>
              <Button size="sm">Small</Button>
              <Button size="lg">Large</Button>
              <Button onClick={() => toast.success("Guardado con éxito")}>Probar toast</Button>
            </div>
          </section>

          {/* Badges + chips */}
          <section className="space-y-3">
            <Eyebrow>Badges & tags</Eyebrow>
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="brand">Activo</Badge>
              <Badge variant="success">Cobrado</Badge>
              <Badge variant="warning">Pendiente</Badge>
              <Badge variant="coral">Por vencer</Badge>
              <Badge variant="danger">Suspendido</Badge>
              <Badge variant="neutral">Neutro</Badge>
              <Badge variant="solid">Solid</Badge>
              <Badge variant="outline">Outline</Badge>
              <Badge variant="tag">nuevo</Badge>
              <span className="deck-chip">US$ · oct-25 → may-26</span>
            </div>
          </section>

          {/* Forms + controls */}
          <div className="grid gap-6 md:grid-cols-2">
            <Card className="p-6">
              <CardTitle className="mb-4">Formulario</CardTitle>
              <div className="space-y-4">
                <div className="space-y-1.5">
                  <Label htmlFor="e">Correo electrónico</Label>
                  <Input id="e" placeholder="nombre@empresa.com" />
                </div>
                <div className="space-y-1.5">
                  <Label>Plan</Label>
                  <Select>
                    <SelectTrigger><SelectValue placeholder="Elegí un plan" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="starter">Starter</SelectItem>
                      <SelectItem value="pro">Pro</SelectItem>
                      <SelectItem value="enterprise">Enterprise</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="m">Mensaje</Label>
                  <Textarea id="m" placeholder="Escribí un mensaje…" />
                </div>
                <div className="flex items-center justify-between rounded-sm bg-secondary px-3.5 py-2.5">
                  <span className="text-sm font-medium">Notificaciones por WhatsApp</span>
                  <Switch checked={on} onCheckedChange={setOn} />
                </div>
              </div>
            </Card>

            <div className="space-y-6">
              <Card className="p-6">
                <CardTitle className="mb-3">Progreso & carga</CardTitle>
                <div className="space-y-4">
                  <Progress value={68} />
                  <div className="flex items-center gap-3"><Spinner /> <span className="text-sm text-muted-foreground">Sincronizando…</span></div>
                  <div className="space-y-2">
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-1/2" />
                  </div>
                </div>
              </Card>

              <Alert variant="brand">
                <Sparkles />
                <AlertTitle>Consejo</AlertTitle>
                <AlertDescription>El gradiente se usa con moderación: héroes, CTA primario o barra de acento.</AlertDescription>
              </Alert>
            </div>
          </div>

          {/* Tabs + overlays */}
          <section className="space-y-3">
            <Eyebrow>Tabs & overlays</Eyebrow>
            <Tabs defaultValue="resumen">
              <TabsList>
                <TabsTrigger value="resumen">Resumen</TabsTrigger>
                <TabsTrigger value="equipo">Equipo</TabsTrigger>
                <TabsTrigger value="ajustes">Ajustes</TabsTrigger>
              </TabsList>
              <TabsContent value="resumen">
                <Card className="p-6 text-sm text-muted-foreground">Contenido del resumen.</Card>
              </TabsContent>
              <TabsContent value="equipo">
                <Card className="p-6 text-sm text-muted-foreground">Contenido del equipo.</Card>
              </TabsContent>
              <TabsContent value="ajustes">
                <Card className="p-6 text-sm text-muted-foreground">Contenido de ajustes.</Card>
              </TabsContent>
            </Tabs>
            <div className="flex flex-wrap items-center gap-3">
              <Dialog>
                <DialogTrigger asChild><Button variant="outline">Abrir diálogo</Button></DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>¿Confirmar acción?</DialogTitle>
                    <DialogDescription>Esta acción se puede deshacer desde el historial.</DialogDescription>
                  </DialogHeader>
                  <DialogFooter>
                    <DialogClose asChild><Button variant="ghost">Cancelar</Button></DialogClose>
                    <Button>Confirmar</Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
              <Tooltip>
                <TooltipTrigger asChild><Button variant="subtle">Con tooltip</Button></TooltipTrigger>
                <TooltipContent>Información útil</TooltipContent>
              </Tooltip>
              <DropdownMenu>
                <DropdownMenuTrigger asChild><Button variant="ghost" size="icon"><MoreHorizontal /></Button></DropdownMenuTrigger>
                <DropdownMenuContent>
                  <DropdownMenuLabel>Acciones</DropdownMenuLabel>
                  <DropdownMenuItem>Editar</DropdownMenuItem>
                  <DropdownMenuItem>Duplicar</DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem className="text-danger">Eliminar</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </section>

          {/* Table */}
          <section className="space-y-3">
            <Eyebrow>Tabla densa</Eyebrow>
            <Card className="overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente</TableHead>
                    <TableHead>Estado</TableHead>
                    <TableHead className="text-right tabular">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {[
                    { n: "Kiosko del Sol", e: "success", l: "Cobrado", m: "US$ 1.240" },
                    { n: "Bar Rivadavia", e: "warning", l: "Pendiente", m: "US$ 890" },
                    { n: "Óptica Central", e: "danger", l: "Suspendido", m: "US$ 2.010" },
                  ].map((r) => (
                    <TableRow key={r.n}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-3">
                          <Avatar className="h-8 w-8"><AvatarFallback>{r.n[0]}</AvatarFallback></Avatar>
                          {r.n}
                        </div>
                      </TableCell>
                      <TableCell><Badge variant={r.e}>{r.l}</Badge></TableCell>
                      <TableCell className="text-right font-semibold tabular">{r.m}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Card>
          </section>

          {/* Empty state */}
          <section className="space-y-3">
            <Eyebrow>Estado vacío</Eyebrow>
            <EmptyState
              icon={Inbox}
              title="Todavía no hay conversaciones"
              description="Cuando tus clientes escriban por WhatsApp van a aparecer acá."
              action={<Button variant="gradient"><MessageSquare /> Conectar WhatsApp</Button>}
            />
          </section>

          <Separator />
          <p className="pb-10 text-center text-sm text-muted-foreground">WhatsPRO · deck-socios design system</p>
        </div>
      </div>
    </TooltipProvider>
  );
}
